import {
    FetchState,
    PartnerUser,
    PartnerUserState,
    PartnerUsersResponse,
    PartnerUserRequest,
    PartnerUserSaveRequest,
    UserRoleModel,
    RoleType,
    OktaFactorType
} from "@/shared/types";
import {
    getPartnerUsers,
    updatePartnerUser,
    resendPartnerUserActivationLink,
    updatePartnerNotificationEmail,
    getPartnerNotificationEmail,
    partnerUserForgotPassword,
    partnerUserDisableMfa,
    updatePartnerUserData,
    getRolesByType,
    getPartnerUserMfaOptions,
    resetPartnerUserMfaOptions
} from "@/shared/api/userService";
import * as storeHelper from "@/shared/storeHelper";
import Vue from "vue";
import _ from "lodash";
import { defineStore } from "pinia";
import { useNotifierStore } from "@/shared/store/notifier";
import { useUserStore } from "@/shared/store/user";

const pageSize = 10;

export const initialState: PartnerUserState = {
    selectedPartnerId: 0,
    pageNumber: 1,
    partnerUserDictionary: {},
    partnerUsersList: [],
    numberOfOwners: 0,
    total: 0,
    notificationEmail: "",
    notificationEmailFetchState: FetchState.Uninitialized
};

function pageNumberToIndex (pageNumber: number) {
    return (pageNumber - 1) * pageSize;
}

export const usePartnerUserStore = defineStore("partnerUser", {
    state: (): PartnerUserState => (_.cloneDeep(initialState)),
    getters: {
        isLoaded: state => partnerId => {
            const fetchState = _.get(state.partnerUserDictionary, `${partnerId}.fetchState`,
                FetchState.Uninitialized) as FetchState;

            return fetchState === FetchState.Loaded;
        },
        isNotificationEmailLoaded: state => state.notificationEmailFetchState === FetchState.Loaded,
        pageRange () {
            return _.range(this.pageNumber - 2, this.pageNumber + 3)
                .filter(page => page > 0 && page <= this.pagesTotal);
        },
        pagesTotal: state => {
            return Math.ceil(state.total / pageSize);
        },
        partnerUsers: state => {
            const partnerUsers = _.get(state.partnerUserDictionary, `${state.selectedPartnerId}.users`, []);
            return partnerUsers;
        },
        partnerUserTotal: state => state.total
    },
    actions: {
        setPartnerUsersFetchState (fetchState: FetchState) {
            const partnerUserState = this.partnerUserDictionary[this.selectedPartnerId];

            if (!partnerUserState) {
                Vue.set(this.partnerUserDictionary, this.selectedPartnerId, {
                    fetchState,
                    users: []
                });
            } else {
                partnerUserState.fetchState = fetchState;
            }
        },
        setPageNumber (pageNumber: number) {
            this.pageNumber = pageNumber;
        },
        setSelectedPartnerId (partnerId: number) {
            this.selectedPartnerId = partnerId;
        },
        setNotificationEmail (email: string) {
            this.notificationEmail = email;
        },
        setNotificationEmailFetchState (fetchState: FetchState) {
            this.notificationEmailFetchState = fetchState;
        },
        setPartnerUsers (response: PartnerUsersResponse) {
            const selectedPartner = _.get(this.partnerUserDictionary, this.selectedPartnerId);
            this.numberOfOwners = response.numberOfOwners;
            this.total = response.total;
            this.pageNumber = response.pageNumber;
            if (selectedPartner === undefined) {
                Vue.set(this.partnerUserDictionary, this.selectedPartnerId, {
                    fetchState: FetchState.Loaded,
                    users: response.users,
                    numberOfOwners: response.numberOfOwners
                });
            } else {
                Vue.set(this.partnerUserDictionary, this.selectedPartnerId, {
                    fetchState: selectedPartner.fetchState,
                    users: response.users,
                    numberOfOwners: response.numberOfOwners
                });
            }
        },
        addPartnerUser (partnerUser: PartnerUser) {
            const selectedPartner = _.get(this.partnerUserDictionary, this.selectedPartnerId);

            if (selectedPartner === undefined) {
                Vue.set(this.partnerUserDictionary, this.selectedPartnerId, {
                    fetchState: FetchState.Loaded,
                    users: [ partnerUser ]
                });
            } else {
                Vue.set(this.partnerUserDictionary, this.selectedPartnerId, {
                    fetchState: selectedPartner.fetchState,
                    users: _.concat(selectedPartner.users, partnerUser)
                });
            }
        },
        updatePartnerUser (partnerUser: PartnerUser) {
            const selectedPartner = _.get(this.partnerUserDictionary, this.selectedPartnerId);

            if (selectedPartner === undefined) {
                Vue.set(this.partnerUserDictionary, this.selectedPartnerId, {
                    fetchState: FetchState.Loaded,
                    users: [ partnerUser ]
                });
            } else {
                const userIndex = selectedPartner.users.findIndex(user => user.inviteId === partnerUser.inviteId);

                if (userIndex > -1) {
                    Vue.set(selectedPartner.users, userIndex, partnerUser);
                }
            }
        },
        updatePartnerUserData (partnerId: number, partnerUser: PartnerUser) {
            return updatePartnerUserData(partnerId, partnerUser).then(response => {
                if (!response.data.error) {
                    this.updatePartnerUser(response.data.partnerUser);
                    return response.data;
                }
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        adjustNumberOfOwners (adjustment: number) {
            this.numberOfOwners += adjustment;
        },
        clearNotificationEmail () {
            this.notificationEmail = "";
            this.notificationEmailFetchState = FetchState.Uninitialized;
        },
        partnerUserSave ({ partnerUser, partnerId }: PartnerUserSaveRequest) {
            return updatePartnerUser(partnerId, partnerUser).then(response => {
                if (!response.data.error) {
                    if (!partnerUser.inviteId) {
                        this.addPartnerUser(response.data.partnerUser);
                    }
                }
                return response.data;
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        partnerNotificationEmailSave (email: string) {
            if (this.notificationEmailFetchState === FetchState.Loading) {
                return Promise.resolve();
            }
            this.setNotificationEmailFetchState(FetchState.Loading);
            const userStore = useUserStore();
            return updatePartnerNotificationEmail(userStore.partnerId, { email, error: null }).then(response => {
                if (response.data) {
                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("Email has been updated.");
                }
                this.setNotificationEmailFetchState(FetchState.Loaded);
                this.setNotificationEmail(email);
                return response.data;
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        loadPartnerNotificationEmail () {
            this.setNotificationEmailFetchState(FetchState.Loading);
            const userStore = useUserStore();
            return getPartnerNotificationEmail(userStore.partnerId).then(response => {
                this.setNotificationEmailFetchState(FetchState.Loaded);
                if (!response.data.error) {
                    this.setNotificationEmail(response.data.email);
                    return response.data.email;
                } else {
                    storeHelper.handleError({ error: response.data.error, rethrow: true });
                }
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        loadPartnerUsers (partnerUserRequest: PartnerUserRequest) {
            this.setSelectedPartnerId(partnerUserRequest.partnerId);
            this.setPartnerUsersFetchState(FetchState.Loading);

            return getPartnerUsers(partnerUserRequest).then(response => {
                this.setPartnerUsersFetchState(FetchState.Loaded);
                this.setPartnerUsers(response.data);
            }).catch(err => {
                this.setPartnerUsersFetchState(FetchState.Uninitialized);
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        resendPartnerUserSignupLink (inviteId: number) {
            const notifierMessage = "Invitation link has been resent successfully!";
            const notifierStore = useNotifierStore();
            resendPartnerUserActivationLink(this.selectedPartnerId, inviteId)
                .then(() => notifierStore.setSuccess(notifierMessage))
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        sendForgotPasswordLink (params: { partnerId: number, email: string }) {
            const notifierStore = useNotifierStore();
            partnerUserForgotPassword(params.partnerId, params.email)
                .then(
                    () => notifierStore.setSuccess("The user has been sent instructions to reset their password."))
                .catch(error => storeHelper.handleError({ error, rethrow: false }));
        },
        sendDisableMfaLink (params: { partnerId: number, email: string }) {
            const notifierStore = useNotifierStore();
            partnerUserDisableMfa(params.partnerId, params.email)
                .then(() => notifierStore.setSuccess("The user has been sent an email to confirm the mfa change."))
                .catch(error => storeHelper.handleError({ error, rethrow: false }));
        },
        loadMfaOptions (params: { partnerId: number, email: string }) {
            return getPartnerUserMfaOptions(params.partnerId, params.email)
              .then(response => response.data)
              .catch(error => storeHelper.handleError({ error, rethrow: true }));
        },
        resetMfaOptions (params: { partnerId: number, email: string, mfaOptions: OktaFactorType[] }) {
            return resetPartnerUserMfaOptions(params.partnerId, params.email, params.mfaOptions)
              .then(() => {
                  const notifier = useNotifierStore();
                  notifier.setSuccess("MFA options successfully reset.");
              })
              .catch(error => storeHelper.handleError({ error, rethrow: true }));
        }
    }
});
